/*
 * pdp-feature.js
 * ---------------------------------------------------------------
 * Plays the feature clip only while it is on screen.
 *
 * The alternative is `autoplay`, which starts every clip on the page at
 * load whether or not anyone reaches it -- on a homepage carrying three
 * of these that is three videos downloaded to show one. This starts a
 * clip when it scrolls into view and pauses it when it leaves.
 *
 * Anyone who has asked their system for reduced motion gets a still
 * poster and controls instead, rather than a loop they did not want.
 *
 * Vanilla ES6, no dependencies.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setup(video) {
    if (video.dataset.featReady === 'true') return;
    video.dataset.featReady = 'true';

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    if (REDUCED) {
      video.controls = true;
      return;
    }

    if (!('IntersectionObserver' in window)) {
      video.play().catch(function () {});
      return;
    }

    /* Warmed before it is needed.
       Loading only once the clip is 35% visible meant the download started
       at the moment it was supposed to be playing, so the first thing a
       visitor saw was a poster and a stall. This starts buffering while it
       is still a screen and a half away, which is far enough to arrive
       ready and near enough that a visitor who never scrolls there pays
       nothing. */
    if (!video.dataset.featWarmed) {
      new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            video.dataset.featWarmed = 'true';
            video.preload = 'auto';
            video.load();
            obs.disconnect();
          });
        },
        { rootMargin: '150% 0px' }
      ).observe(video);
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            /* `play()` rejects if the browser declines -- an unmuted
               clip, a data-saver setting, a background tab. Showing the
               poster is a fine outcome, so the rejection is swallowed
               rather than logged.

               If it rejects because nothing has buffered yet, the
               `canplay` handler below picks it up. */
            video.play().catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );

    io.observe(video);

    /* The retry. A first `play()` on an empty buffer fails silently and
       the clip then sits on its poster forever, which is the "sometimes it
       just doesn't start" fault. When enough has arrived to play, try
       again -- but only if it is still on screen. */
    video.addEventListener('canplay', function () {
      var box = video.getBoundingClientRect();
      var onScreen = box.top < window.innerHeight && box.bottom > 0;
      if (onScreen && video.paused) video.play().catch(function () {});
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('.feat__media video').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
